import { useEffect, useState } from "react";
import { getMrcHistoryReport } from "../services/analyticsService";

function useDebouncedValue(value, delayMs = 300) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

export const useMrcHistoryReport = (tenantId, year, options = {}) => {
  const {
    enabled = true,
    page = 1,
    limit = 20,
    search = "",
    changeFilter = "all",
    provider = "all",
    category = "all",
    sortKey = null,
    sortDir = "asc",
  } = options;

  const debouncedSearch = useDebouncedValue(search, 300);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!enabled || !tenantId || !year) {
      setData(null);
      setError(null);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const response = await getMrcHistoryReport(tenantId, year, {
          page,
          limit,
          search: debouncedSearch,
          changeFilter,
          provider,
          category,
          sortKey,
          sortDir,
        });
        if (cancelled) return;
        if (response?.success && response?.data != null) {
          setData(response.data);
        } else {
          setData(null);
          setError(new Error(response?.message || "Failed to load MRC history report"));
        }
      } catch (err) {
        if (cancelled) return;
        setData(null);
        setError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    tenantId,
    year,
    enabled,
    page,
    limit,
    debouncedSearch,
    changeFilter,
    provider,
    category,
    sortKey,
    sortDir,
  ]);

  const refresh = () => {
    if (!enabled || !tenantId || !year) return Promise.resolve();
    setLoading(true);
    setError(null);
    return getMrcHistoryReport(tenantId, year, {
      page,
      limit,
      search: debouncedSearch,
      changeFilter,
      provider,
      category,
      sortKey,
      sortDir,
      refresh: true,
    })
      .then((response) => {
        if (response?.success && response?.data != null) {
          setData(response.data);
        } else {
          setData(null);
          setError(new Error(response?.message || "Failed to load MRC history report"));
        }
      })
      .catch((err) => {
        setData(null);
        setError(err);
      })
      .finally(() => setLoading(false));
  };

  return { data, loading, error, refresh };
};
